// src/components/schedule/ScheduleList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import SyncScheduleService from '../../services/SyncScheduleService';
import ScheduleModal from './ScheduleModal';
import { formatDateTimeForDisplay } from '../../utils/dateUtils';
import { toast } from 'react-toastify';

/* ── Helpers ─────────────────────────────────────────────── */
const TYPE_META = {
  all:                          { icon: 'bi-arrow-repeat',        label: 'All Data' },
  courses:                      { icon: 'bi-book',                label: 'Courses' },
  chapters:                     { icon: 'bi-list-nested',         label: 'Chapters' },
  content:                      { icon: 'bi-file-earmark-text',   label: 'Chapter Content' },
  'live-class-content':         { icon: 'bi-camera-video',        label: 'Live Class Content' },
  'jee-live-class-content':     { icon: 'bi-camera-video',        label: 'JEE Live Class Content' },
  mentors:                      { icon: 'bi-person',              label: 'Mentors' },
  exams:                        { icon: 'bi-card-list',           label: 'Exams' },
  attempts:                     { icon: 'bi-clipboard-check',     label: 'Attempts' },
  batches:                      { icon: 'bi-people',              label: 'Batches' },
  users:                        { icon: 'bi-people-fill',         label: 'Users' },
  'content-attempts':           { icon: 'bi-pencil-square',       label: 'Content Attempts' },
  'exam-attempts':              { icon: 'bi-card-checklist',      label: 'Exam Attempts' },
  'attempt-answers':            { icon: 'bi-record-circle',       label: 'Attempt Answers' },
  'user-conference-attempts':   { icon: 'bi-camera-video',        label: 'User Conference' },
  'video-conference':           { icon: 'bi-camera-video',        label: 'Video Conference' },
  'single-attempts':            { icon: 'bi-arrow-repeat',        label: 'Each Attempt' },
};

const getMeta = (dataType) =>
  TYPE_META[dataType] || { icon: 'bi-clock', label: dataType || 'Unknown' };

/** Build a human-readable cron string from schedule fields */
const buildCronLabel = (schedule) => {
  const { frequency, startTime, weekDays } = schedule;
  if (!frequency) return '—';

  const time = startTime || '00:00';

  switch (frequency) {
    case 'hourly':
      return `Every hour at :${time.split(':')[1] || '00'}`;
    case 'daily':
      return `Daily at ${time}`;
    case 'weekly': {
      if (!weekDays) return `Weekly at ${time}`;
      const dayNames = { sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };
      const days = Object.entries(weekDays)
        .filter(([, v]) => v)
        .map(([k]) => dayNames[k] || k)
        .join(', ');
      return days ? `${days} at ${time}` : `Weekly at ${time}`;
    }
    case 'monthly':
      return `Monthly (day ${schedule.dayOfMonth || 1}) at ${time}`;
    case 'once':
      return `Once at ${time}`;
    default:
      return frequency;
  }
};

/* ── ActiveToggle ────────────────────────────────────────── */
const ActiveToggle = ({ schedule, onToggled }) => {
  const [busy, setBusy] = useState(false);

  const handleChange = async () => {
    setBusy(true);
    try {
      await SyncScheduleService.updateSchedule(schedule.id, {
        ...schedule,
        enabled: !schedule.enabled,
      });
      toast.success(
        `Schedule ${schedule.enabled ? 'paused' : 'activated'} successfully`
      );
      if (onToggled) onToggled();
    } catch {
      toast.error('Failed to update schedule status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <label
      className="schedule-toggle"
      aria-label={schedule.enabled ? 'Pause schedule' : 'Activate schedule'}
      title={schedule.enabled ? 'Click to pause' : 'Click to activate'}
    >
      <input
        type="checkbox"
        checked={!!schedule.enabled}
        onChange={handleChange}
        disabled={busy}
      />
      <span className="schedule-toggle-track" />
      <span className="schedule-toggle-thumb" />
    </label>
  );
};

/* ── ScheduleList ────────────────────────────────────────── */
const ScheduleList = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null); // null = create

  // Delete confirmation
  const [deletingId, setDeletingId] = useState(null);

  /* ── Fetch ─────────────────────────────────────────────── */
  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await SyncScheduleService.getSchedulesWithLastSync();
      if (res?.success) {
        setSchedules(res.data || []);
      } else {
        setSchedules([]);
      }
    } catch {
      toast.error('Failed to load schedules');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  /* ── Handlers ──────────────────────────────────────────── */
  const openCreate = () => {
    setEditingSchedule(null);
    setShowModal(true);
  };

  const openEdit = (schedule) => {
    setEditingSchedule(schedule);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSchedule(null);
  };

  const handleSaved = () => {
    fetchSchedules();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await SyncScheduleService.deleteSchedule(id);
      toast.success('Schedule deleted');
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    } catch {
      toast.error('Failed to delete schedule');
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Render ────────────────────────────────────────────── */
  return (
    <>
      <div className="theme-card schedule-list-card">
        {/* Header */}
        <div className="schedule-list-header">
          <div className="schedule-list-title">
            <i className="bi bi-calendar-event" style={{ color: 'var(--accent-blue)' }} aria-hidden="true" />
            Scheduled Syncs
            {!loading && (
              <span className="schedule-count-badge">{schedules.length}</span>
            )}
          </div>
          <button
            className="qa-btn qa-btn--blue"
            onClick={openCreate}
            style={{ fontSize: '0.8125rem', padding: '7px 14px' }}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add Schedule
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="schedule-loading">
            {[1, 2, 3].map((n) => (
              <div key={n} className="schedule-loading-row" />
            ))}
          </div>
        ) : schedules.length === 0 ? (
          <div className="schedule-empty">
            <i className="bi bi-calendar-x schedule-empty-icon" aria-hidden="true" />
            <span className="schedule-empty-text">No schedules configured</span>
            <button className="qa-btn qa-btn--ghost" onClick={openCreate} style={{ marginTop: 4 }}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Create your first schedule
            </button>
          </div>
        ) : (
          <div className="schedule-table-wrapper">
            <table className="schedule-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Schedule</th>
                  <th>Last Run</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => {
                  const meta = getMeta(schedule.dataType);
                  const isDeleting = deletingId === schedule.id;

                  return (
                    <tr key={schedule.id} style={{ opacity: isDeleting ? 0.5 : 1 }}>
                      {/* Type */}
                      <td>
                        <div className="schedule-type-cell">
                          <i
                            className={`bi ${meta.icon} schedule-type-icon`}
                            style={{ color: 'var(--accent-blue)' }}
                            aria-hidden="true"
                          />
                          <span className="schedule-type-name">{meta.label}</span>
                        </div>
                      </td>

                      {/* Schedule (frequency + time) */}
                      <td>
                        <span className="schedule-cron">{buildCronLabel(schedule)}</span>
                      </td>

                      {/* Last run */}
                      <td>
                        {schedule.lastSync ? (
                          <div>
                            <span className="schedule-time">
                              {formatDateTimeForDisplay(schedule.lastSync.startTime)}
                            </span>
                            <span
                              className="schedule-time-muted"
                              style={{
                                color:
                                  schedule.lastSync.status === 'failed'
                                    ? 'var(--accent-red)'
                                    : schedule.lastSync.status === 'completed_with_errors'
                                    ? 'var(--accent-orange)'
                                    : 'var(--text-muted)',
                              }}
                            >
                              {schedule.lastSync.status === 'failed'
                                ? 'Failed'
                                : schedule.lastSync.status === 'completed_with_errors'
                                ? 'Completed with errors'
                                : 'Completed'}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', fontStyle: 'italic' }}>
                            Never run
                          </span>
                        )}
                      </td>

                      {/* Active toggle */}
                      <td>
                        <ActiveToggle schedule={schedule} onToggled={fetchSchedules} />
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'right' }}>
                        <div className="schedule-row-actions" style={{ justifyContent: 'flex-end' }}>
                          <button
                            className="schedule-action-btn schedule-action-btn--edit"
                            onClick={() => openEdit(schedule)}
                            title="Edit schedule"
                            aria-label="Edit schedule"
                            disabled={isDeleting}
                          >
                            <i className="bi bi-pencil" aria-hidden="true" />
                          </button>
                          <button
                            className="schedule-action-btn schedule-action-btn--delete"
                            onClick={() => handleDelete(schedule.id)}
                            title="Delete schedule"
                            aria-label="Delete schedule"
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <span className="schedule-spinner" aria-hidden="true" />
                            ) : (
                              <i className="bi bi-trash" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Schedule create/edit modal */}
      {showModal && (
        <ScheduleModal
          schedule={editingSchedule}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

export default ScheduleList;
